import * as React from "react";
import { useMachine } from "@xstate/react";
import { machine } from "./Combobox.machine";
import useOnclickOutside from "react-cool-onclickoutside";
import { css, Variants, cx } from "@nju/css";
import scrollIntoView from "scroll-into-view-if-needed";
import { InputStateStyleProp, styles as inputStyles } from "../Input";
import { Button } from "@nju/ui";

interface IComboboxProps<Item> extends InputStateStyleProp {
  items: Item[];
  onSelect: (values: Item[]) => void;
  className?: string;
  isDisabled?: boolean;
  maxListHeight?: number | string;
  uniqueItemId?: (item: Item) => string;
  prefix?:
    | React.ReactNode
    | ((_: {
        isOpen: boolean;
        items: Item[];
        toggle(): void;
        reset(): void;
      }) => React.ReactNode);
  suffix?:
    | React.ReactNode
    | ((_: {
        isOpen: boolean;
        items: Item[];
        toggle(): void;
        reset(): void;
      }) => React.ReactNode);
  variant?: "select" | "combo";
  listColorVariant?: Variants<typeof styles["container"]>["variant"];
  label?: string;
  defaultValue?: Item[];
  multiple?: boolean;
  openOnFocus?: boolean;
  inputDisplayValue?: (values: Item[]) => string;
  listItemDisplayValue?: (item: Item) => string;
  onInputChange?: (value: string) => void;
  itemComparator?: (firs: Item, second: Item) => boolean;
  reset?: {
    enabled: boolean;
    setReset: (value: boolean) => void;
  };
  withReset?: boolean;
  testId?: string;

  listItemRenderer?: (_: {
    item: Item;
    index: number;
    isActive: boolean;
    isSelected: boolean;
  }) => React.ReactNode;
}

export function Combobox<T>({
  state,
  suffix,
  prefix,
  onInputChange,
  onSelect,
  className,
  label,
  items,
  testId,
  isDisabled = false,
  maxListHeight = 240,
  listColorVariant = "white",
  withReset = false,
  uniqueItemId = (item) => {
    return typeof item === "string" ? item : "";
  },
  listItemRenderer,
  variant = "combo",
  multiple = false,
  openOnFocus = false,
  defaultValue = [],
  reset,
  inputDisplayValue = (items) => {
    return items.join(", ");
  },
  listItemDisplayValue = (item: unknown) => {
    return typeof item === "string" ? item : "";
  },
  itemComparator = (first, second) => {
    return first === second;
  },
}: IComboboxProps<T>) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const [{ context, matches, tags }, send] = useMachine(machine, {
    context: {
      isDisabled,
      openOnFocus,
      // @ts-expect-error Type 'unknown[]' is not assignable to type 'T[]'.
      itemComparator,
      // @ts-expect-error Type 'unknown[]' is not assignable to type 'T[]'.
      inputDisplayValue,
      currentValues: defaultValue,
      inputValue: defaultValue ? inputDisplayValue(defaultValue) : "",
      multiple,
      items,
      variant,
    },
    actions: {
      triggerOnInputValueChange: (context) => {
        if (onInputChange) {
          onInputChange(context.inputValue);
        }
      },
      triggerOnItemSelect: (context) => {
        onSelect(context.currentValues as T[]);
        inputRef.current?.focus();
        triggerRef.current?.focus();
      },
      focusInputInDOM: () => {
        inputRef.current?.focus();
      },
      focusTriggerInDOM: () => {
        triggerRef.current?.focus();
      },
      blurInputInDOM: () => {
        inputRef.current?.blur();
      },
      blurTriggerInDOM: () => {
        triggerRef.current?.blur();
      },
      triggerListIndexChange: (context) => {
        requestAnimationFrame(() => {
          const element = listRef.current?.querySelector(
            `:nth-child(${context.activeListIndex + 1})`
          );

          if (element) {
            scrollIntoView(element, {
              behavior: "auto",
              scrollMode: "if-needed",
              block: "center",
              boundary: listRef.current,
            });
          }
        });
      },
    },
  });

  const wrapperRef = useOnclickOutside(() => {
    send({ type: "DEACTIVATE" });
  });

  React.useEffect(() => {
    if (isDisabled) {
      send({ type: "DISABLE" });
    } else {
      send({ type: "ENABLE" });
    }
  }, [isDisabled, send]);

  React.useEffect(() => {
    send({ type: "ITEMS.SET", value: items });
  }, [items, send]);

  React.useEffect(() => {
    if (!reset?.enabled) {
      return;
    }

    send({ type: "RESET" });
    reset.setReset(false);
  }, [reset, send]);

  return (
    <div
      className={cx("relative", className)}
      data-cy={testId}
      ref={wrapperRef}
    >
      {variant === "select" && (
        <div
          className={inputStyles.wrapper({
            focus: tags.has("active"),
            disabled: isDisabled,
            state,
          })}
        >
          {prefix && (
            <span className={inputStyles.prefix()}>
              {typeof prefix === "function"
                ? prefix({
                    isOpen: matches("expanded"),
                    items: context.currentValues as T[],
                    toggle: () => {
                      send({ type: "TOGGLE" });
                    },
                    reset: () => {
                      send({ type: "RESET" });
                    },
                  })
                : prefix}
            </span>
          )}
          <div className={inputStyles.inputWrapper()}>
            {label ? (
              <label
                className={inputStyles.label({
                  offside: !!context.inputValue,
                })}
              >
                {label}
              </label>
            ) : undefined}
            <input
              className={inputStyles.input()}
              data-cy={testId + "-input"}
              disabled={isDisabled}
              onBlur={() => {
                requestAnimationFrame(() => {
                  send({ type: "TRIGGER.BLUR" });
                });
              }}
              onFocus={() => {
                send({ type: "TRIGGER.FOCUS" });
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  send({ type: "LIST.CLOSE" });
                }
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  send({ type: "ITEMS.SELECT_ACTIVE_INDEX" });
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_PREV_INDEX" });
                }
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_NEXT_INDEX" });
                }
                if (e.key === "Home") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_FIRST" });
                }
                if (e.key === "End") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_LAST" });
                }
                if (e.key === "Tab") {
                  e.preventDefault();
                  send({ type: "ITEMS.SELECT_ACTIVE_INDEX" });
                }
              }}
              onPointerDown={() => {
                if (matches("expanded")) {
                  send({ type: "LIST.CLOSE" });
                } else {
                  send({ type: "LIST.OPEN" });
                }
              }}
              readOnly={true}
              ref={triggerRef}
              value={inputDisplayValue(context.currentValues as T[]) || ""}
            />
          </div>
          {suffix && (
            <span className={inputStyles.suffix()} data-cy={testId + "-button"}>
              {typeof suffix === "function"
                ? suffix({
                    isOpen: matches("expanded"),
                    items: context.currentValues as T[],
                    toggle: () => {
                      send({ type: "TOGGLE" });
                    },
                    reset: () => {
                      send({ type: "RESET" });
                    },
                  })
                : suffix}
            </span>
          )}
        </div>
      )}
      {variant === "combo" && (
        <div
          className={inputStyles.wrapper({
            focus: tags.has("active"),
            disabled: isDisabled,
            state,
          })}
        >
          {prefix && (
            <span className={inputStyles.prefix()}>
              {typeof prefix === "function"
                ? prefix({
                    isOpen: matches("expanded"),
                    items: context.currentValues as T[],
                    toggle: () => {
                      send({ type: "TOGGLE" });
                    },
                    reset: () => {
                      send({ type: "RESET" });
                    },
                  })
                : prefix}
            </span>
          )}
          <div className={inputStyles.inputWrapper({})}>
            {label ? (
              <label
                className={inputStyles.label({
                  offside: !!context.inputValue,
                })}
              >
                {label}
              </label>
            ) : undefined}
            <input
              className={inputStyles.input({})}
              disabled={isDisabled}
              onBlur={() => {
                send({ type: "INPUT.BLUR" });
              }}
              onChange={(e) => {
                send({ type: "INPUT.CHANGE", value: e.target.value });
              }}
              onFocus={() => {
                send({ type: "INPUT.FOCUS" });
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_PREV_INDEX" });
                }
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_NEXT_INDEX" });
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  send({ type: "LIST.CLOSE" });
                }
                if (e.key === "Home") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_FIRST" });
                }
                if (e.key === "End") {
                  e.preventDefault();
                  send({ type: "LIST.SELECT_LAST" });
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  send({ type: "ITEMS.SELECT_ACTIVE_INDEX" });
                }
                if (e.key === "Tab") {
                  // e.preventDefault();
                  send({ type: "ITEMS.SELECT_ACTIVE_INDEX" });
                }
              }}
              ref={inputRef}
              value={context.inputValue}
            />
          </div>
          {suffix && (
            <span className={inputStyles.suffix()}>
              {typeof suffix === "function"
                ? suffix({
                    isOpen: matches("expanded"),
                    items: context.currentValues as T[],
                    toggle: () => {
                      send({ type: "TOGGLE" });
                    },
                    reset: () => {
                      send({ type: "RESET" });
                    },
                  })
                : suffix}
            </span>
          )}
        </div>
      )}
      {matches("expanded") && items?.length > 0 && (
        <div
          className={styles.container({ variant: listColorVariant })}
          ref={listRef}
          style={{ maxHeight: maxListHeight }}
        >
          {items.map((item, index) => {
            const isActive = index === context.activeListIndex;
            const isSelected = context.currentValues.some((itemFromList) => {
              return itemComparator(item, itemFromList as T);
            });

            const content = listItemRenderer
              ? listItemRenderer({
                  item,
                  index,
                  isActive,
                  isSelected,
                })
              : listItemDisplayValue(item);
            return (
              <div
                className={styles.item({
                  selected: isSelected,
                  highlighted: isActive,
                  variant: listColorVariant,
                })}
                key={uniqueItemId(item)}
                onClick={() => {
                  send({ type: "ITEMS.SELECT", value: item });
                }}
              >
                {content}
              </div>
            );
          })}
          {withReset && (
            <div className={styles.reset()}>
              <Button
                onPress={() => {
                  return send({ type: "RESET" });
                }}
                size="inline"
                variant="simple"
                withUnderline={true}
              >
                wyzeruj
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: css({
    top: "120%",
    overflow: "auto",
    position: "absolute",
    zIndex: "$listbox",
    width: "100%",
    backgroundColor: "$white",
    borderRadius: "$12",
    scrollBehavior: "smooth",
    variants: {
      variant: {
        white: {
          border: "2px solid $geyser",
        },
        gray: {},
      },
    },
  }),
  item: css({
    cursor: "pointer",
    padding: "$15",
    "&:hover": {
      backgroundColor: "$geyser",
    },
    variants: {
      highlighted: {
        true: {},
      },
      variant: {
        white: {
          backgroundColor: "$white",
        },
        gray: {
          backgroundColor: "$porcelain",
        },
      },

      selected: {
        true: {},
      },
    },
    compoundVariants: [
      {
        variant: "white",
        selected: true,
        css: {
          backgroundColor: "$ripeLemon",
        },
      },
      {
        variant: "gray",
        selected: true,
        css: {
          backgroundColor: "$ripeLemon",
        },
      },
      {
        variant: "white",
        highlighted: true,
        css: {
          backgroundColor: "$geyser",
        },
      },
      {
        variant: "gray",
        highlighted: true,
        css: {
          backgroundColor: "$geyser",
        },
      },
    ],
  }),
  iconPointer: css({
    display: "flex",
  }),
  reset: css({
    px: "$15",
    py: "$5",
  }),
};
